import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { ApiAuthService, AuthService } from '@shared';
import { firstValueFrom } from 'rxjs';

/**
 * Auth guard that requires user to be authenticated
 * Redirects to login if not authenticated
 * 
 * NOTE: The user app allows ANY authenticated user regardless of role.
 * This means vendors, admins, delivery agents, and regular users can all
 * use the user app to browse and order products. Role-based restrictions
 * are handled in the vendor/admin/delivery apps, not here.
 * 
 * Uses JWT token authentication (synced from Firebase Auth after OTP login)
 */
export const authGuard: CanActivateFn = async (route, state): Promise<boolean | UrlTree> => {
  const router = inject(Router);
  const apiAuthService = inject(ApiAuthService);
  const authService = inject(AuthService);

  try {
    // Check if JWT token exists and is valid (not expired)
    if (!apiAuthService.isAuthenticated()) {
      // If token exists but is expired, try to refresh
      const refreshToken = apiAuthService.getRefreshToken();
      if (refreshToken) {
        try {
          await firstValueFrom(apiAuthService.refreshToken());
          // Token refreshed successfully, continue with auth check
        } catch (error) {
          // Refresh failed, redirect to login
          apiAuthService.clearTokens();
          return router.createUrlTree(['/login']);
        }
      } else {
        // No token or refresh token, redirect to login
        return router.createUrlTree(['/login']);
      }
    }

    // Token exists and is valid - check if profile is loaded
    let profile = authService.profile();
    
    // If profile not loaded, try to load from JWT user data
    if (!profile) {
      const jwtUser = apiAuthService.getUser();
      if (jwtUser) {
        const userProfile = apiAuthService.convertToUserProfile(jwtUser);
        authService.profile.set(userProfile);
        profile = userProfile;
      }
    }

    // Require account setup (name) before allowing access to main app
    const targetUrl = state.url.split('?')[0];
    if (profile && !targetUrl.startsWith('/account-setup')) {
      const name = profile.name?.trim();
      if (!name) {
        return router.createUrlTree(['/account-setup']);
      }
    }

    // User is authenticated (has valid JWT token) - allow access regardless of role
    // (vendors, admins, delivery agents can all use the user app)
    return true;
  } catch (error) {
    // On error, assume not authenticated and redirect to login
    apiAuthService.clearToken();
    return router.createUrlTree(['/login']);
  }
};

/**
 * Guard that redirects authenticated users away from login/register pages
 * Uses JWT token to check authentication status
 */
export const redirectIfAuthenticated: CanActivateFn = async (route, state): Promise<boolean | UrlTree> => {
  const router = inject(Router);
  const apiAuthService = inject(ApiAuthService);
  const authService = inject(AuthService);

  try {
    // Check if JWT token exists
    if (!apiAuthService.isAuthenticated()) {
      // Not authenticated - allow access to login/register
      return true;
    }

    // User is authenticated - redirect to home or account-setup if name missing
    
    // Ensure profile is loaded
    let profile = authService.profile();
    if (!profile) {
      const jwtUser = apiAuthService.getUser();
      if (jwtUser) {
        const userProfile = apiAuthService.convertToUserProfile(jwtUser);
        authService.profile.set(userProfile);
        profile = userProfile;
      } else {
        profile = authService.profile();
      }
    }

    // If profile has no name, send to account setup first
    if (profile && !profile.name?.trim()) {
      return router.createUrlTree(['/account-setup']);
    }

    return router.createUrlTree(['/tabs/home']);
  } catch (error) {
    // On error, allow access (user can try to login)
    return true;
  }
};

